import {
    normalizeSearchText,
    GetSettingsFromDataAttributes,
    GetMarkedElements,
    GetSearchSummaryElements,
    GetTermsFromPath
} from './search-utils.js';

(function (window, undefined) {
    "use strict";

    const HEADER_KEY = {
        WpTotal: "X-Wp-Total",
        WpPages: "X-Wp-Totalpages"
    };

    const SEARCH_CLASS = {
        Container: "adf-custom-search",
        Input: "adf-custom-search-input",
        Types: "adf-custom-search-types",
        DateStart: "adf-custom-search-date-start",
        DateEnd: "adf-custom-search-date-end",
        GoBtn: "adf-custom-search-go",
        Results: "adf-custom-search-results",
        PaginationButtons: "adf-custom-search-pagination-buttons",
        TypeBadge: "adf-custom-search-type-badge"
    };

    // JS-1: adf-globals provides Ready(); guard the entry so a load-order failure degrades
    // to DOMContentLoaded with a clear console error instead of a bare ReferenceError.
    if (typeof Ready === "function") {
        Ready(InitializeCustomSearch);
    } else {
        document.addEventListener("DOMContentLoaded", InitializeCustomSearch);
    }

    // The theme paints a checked checkbox with a literal Bootstrap blue, so it can't be
    // overridden inline — inject one scoped rule to brand them ADF navy (--adf-dark-blue):
    // navy outline when unchecked (like .adf-btn-variant-1), navy fill when checked (variant-2).
    function InjectBrandStyles() {
        const id = "adf-custom-search-brand-styles";
        if (document.getElementById(id)) return;
        const navy = "var(--adf-dark-blue, #003057)";
        const box = "." + SEARCH_CLASS.Container + " .form-check-input." + SEARCH_CLASS.Types;
        const style = document.createElement("style");
        style.id = id;
        style.textContent =
            box + "{border-color:" + navy + "}" +
            box + ":checked{background-color:" + navy + ";border-color:" + navy + "}";
        document.head.appendChild(style);
    }

    function InitializeCustomSearch() {
        // JS-1: fail loudly but gracefully if the adf-globals (adf-global-libs) dependencies
        // this widget relies on never loaded, instead of throwing a bare ReferenceError mid-render.
        if (typeof BuildElement === "undefined" || typeof EmptyElement === "undefined" ||
            typeof Rest === "undefined" || typeof REST_VERBS === "undefined" ||
            typeof DateTime === "undefined" || typeof Pagination === "undefined" ||
            typeof GetLoadingElement === "undefined") {
            if (typeof console !== "undefined") {
                console.error("ADF Search: adf-globals dependencies (adf-global-libs) are not loaded; search was not initialized.");
            }
            return;
        }

        InjectBrandStyles();

        const searchContainers = document.querySelectorAll("." + SEARCH_CLASS.Container);

        for (let c = 0; c < searchContainers.length; c++) {
            const containerEl = searchContainers[c];

            // Read settings from data attributes
            const settings = GetSettingsFromDataAttributes(containerEl);
            if (!settings) continue;

            // Render the form HTML
            RenderSearchForm(containerEl, settings);

            // Initialize form handlers
            InitializeFormHandlers(containerEl, settings);
        }
    }

    function RenderSearchForm(containerEl, settings) {
        const instance = settings.instance;

        // Create form element
        const form = BuildElement("form", "", []);
        form.addEventListener("submit", function(e) { e.preventDefault(); });

        // Post type checkboxes (if enabled and multiple types)
        if (settings.showTypeFilters && settings.postTypes.length > 1) {
            form.appendChild(BuildPostTypeFieldset(settings));
        }

        // Date filters (if enabled)
        if (settings.showDateFilters) {
            form.appendChild(BuildDateFiltersRow(settings));
        }

        // Search input and button
        form.appendChild(BuildSearchInputRow(settings));

        // Results container
        form.appendChild(BuildElement("div", "", ["col", SEARCH_CLASS.Results], { role: "status", "aria-live": "polite" }, "\u00A0"));

        // Pagination container
        form.appendChild(BuildElement("div", "", ["col", SEARCH_CLASS.PaginationButtons, "text-center", "mt-2"], {}, "\u00A0"));

        containerEl.appendChild(form);
    }

    function BuildPostTypeFieldset(settings) {
        const instance = settings.instance;
        const checkboxes = [];

        settings.postTypes.forEach(function(type) {
            const id = "adf-custom-search-types-" + type + instance;
            const label = settings.postTypeLabels[type] || type;

            const checkbox = BuildElement("input", id, [SEARCH_CLASS.Types, "form-check-input"], {
                type: "checkbox",
                name: "adf-custom-search-types",
                value: type
            });
            checkbox.checked = true;

            checkboxes.push(
                BuildElement("div", "", ["form-check", "form-check-inline"], {}, [
                    checkbox,
                    BuildElement("label", "", ["form-check-label"], { for: id }, label)
                ])
            );
        });

        return BuildElement("div", "", ["col", "mb-1"], {}, [
            BuildElement("fieldset", "", ["d-block", "bg-white", "border", "border-secondary", "border-opacity-25", "rounded", "px-3", "pt-2", "pb-1"], {}, [
                BuildElement("legend", "", ["col-form-label-sm", "col-12", "p-0", "m-0", "text-secondary"], {}, "Search In"),
                BuildElement("div", "", ["col-12", "p-0", "m-0"], {}, checkboxes)
            ])
        ]);
    }

    function BuildDateFiltersRow(settings) {
        const instance = settings.instance;
        const startId = "adf-custom-search-date-start" + instance;
        const endId = "adf-custom-search-date-end" + instance;

        return BuildElement("div", "", ["row", "g-1", "mb-1"], {}, [
            BuildElement("div", "", ["form-floating", "col-md-6"], {}, [
                BuildElement("input", startId, [SEARCH_CLASS.DateStart, "form-control", "bg-white", "fs-5"], {
                    type: "date",
                    placeholder: "mm/dd/yyyy"
                }),
                BuildElement("label", "", ["col-form-label-sm", "text-secondary"], { for: startId }, "From Date")
            ]),
            BuildElement("div", "", ["form-floating", "col-md-6"], {}, [
                BuildElement("input", endId, [SEARCH_CLASS.DateEnd, "form-control", "bg-white", "fs-5"], {
                    type: "date",
                    placeholder: "mm/dd/yyyy"
                }),
                BuildElement("label", "", ["col-form-label-sm", "text-secondary"], { for: endId }, "Through Date")
            ])
        ]);
    }

    function BuildSearchInputRow(settings) {
        const instance = settings.instance;
        const inputId = "adf-custom-search-input" + instance;

        return BuildElement("div", "", ["row", "g-1", "mb-1"], {}, [
            BuildElement("div", "", ["col-8", "col-md-10"], {}, [
                BuildElement("div", "", ["form-floating"], {}, [
                    BuildElement("input", inputId, [SEARCH_CLASS.Input, "form-control", "bg-white", "fs-5"], {
                        type: "text",
                        placeholder: "freedom of speech"
                    }),
                    BuildElement("label", "", ["col-form-label-sm", "text-secondary"], { for: inputId }, "Search Text")
                ])
            ]),
            BuildElement("div", "", ["col-4", "col-md-2"], {}, [
                BuildElement("button", "", [SEARCH_CLASS.GoBtn, "btn", "btn-dark", "p-3", "d-block", "w-100"], {
                    type: "button"
                }, "Search")
            ])
        ]);
    }

    function InitializeFormHandlers(containerEl, settings) {
        const opts = {
            InputEl: containerEl.querySelector("input." + SEARCH_CLASS.Input),
            TypesEls: containerEl.querySelectorAll("input." + SEARCH_CLASS.Types),
            DateStartEl: containerEl.querySelector("." + SEARCH_CLASS.DateStart),
            DateEndEl: containerEl.querySelector("." + SEARCH_CLASS.DateEnd),
            GoBtnEl: containerEl.querySelector("." + SEARCH_CLASS.GoBtn),
            ResultsEl: containerEl.querySelector("." + SEARCH_CLASS.Results),
            PaginationButtonsEl: containerEl.querySelector("." + SEARCH_CLASS.PaginationButtons),
            DefaultResultsText: "Type some text to search.",
            Settings: settings
        };

        if ((opts.InputEl === null) || (opts.ResultsEl === null)) {
            containerEl.appendChild(GetResultsMessageElement("Sorry, this search box does not appear to setup correctly.", ["text-danger"], false));
            return;
        }

        opts.ResultsEl.appendChild(GetResultsMessageElement(opts.DefaultResultsText, ["text-secondary"], false));

        // Type checkboxes listeners
        for (let e = 0; e < opts.TypesEls.length; e++) {
            opts.TypesEls[e].addEventListener("change", function() {
                RunAjaxSearch(opts);
            });
        }

        // Date start listener
        if (opts.DateStartEl !== null) {
            opts.DateStartEl.addEventListener("change", function() {
                RunAjaxSearch(opts);
            });
        }

        // Date end listener
        if (opts.DateEndEl !== null) {
            opts.DateEndEl.addEventListener("change", function() {
                RunAjaxSearch(opts);
            });
        }

        // Go button listener
        if (opts.GoBtnEl !== null) {
            opts.GoBtnEl.addEventListener("click", function() {
                RunAjaxSearch(opts);
            });
        }

        // Search input listener and query/404 prefill
        if (opts.InputEl !== null) {
            opts.InputEl.addEventListener("keyup", function(event) {
                if (event.key === "Enter" || event.keyCode === 13) {
                    RunAjaxSearch(opts);
                }
            });
            CheckForPrefill(opts);
        }
    }

    function CheckForPrefill(opts) {
        const searchParams = new URLSearchParams(window.location.search);

        // If this is a 404 page, derive search terms from the URL path
        if (document.querySelector("body.error404") !== null) {
            opts.InputEl.value = GetTermsFromPath(window.location.pathname);

            if (opts.InputEl.value.length > 0) {
                RunAjaxSearch(opts);
            }
        }

        // If the search query is in the query string, prefill
        else if (searchParams.has("q")) {
            opts.InputEl.value = searchParams.get("q");
            RunAjaxSearch(opts);
        }
    }

    function RunAjaxSearch(opts, page, Callback) {

        // Get user values and build URL
        const queries = new URLSearchParams();

        // Search text
        let searchText = opts.InputEl.value.trim();
        if (searchText === "") {
            // JS-6: supersede any in-flight request (so a slow earlier response bails instead of
            // rendering over this), and clear the fixed height/overflow a prior search pinned.
            opts._reqSeq = (opts._reqSeq || 0) + 1;
            opts.ResultsEl.style.height = "auto";
            opts.ResultsEl.style.overflow = "auto";
            EmptyElement(opts.ResultsEl);
            opts.ResultsEl.appendChild(GetResultsMessageElement(opts.DefaultResultsText, ["text-secondary"], false));
            EmptyElement(opts.PaginationButtonsEl);
        }
        else {
            // Keep results height during retrieval then clear out previous results
            opts.ResultsEl.style.height = opts.ResultsEl.getBoundingClientRect().height + "px";
            opts.ResultsEl.style.overflow = "hidden";
            EmptyElement(opts.ResultsEl);

            opts.ResultsEl.appendChild(GetLoadingElement({ Message: " Searching…" }));
            queries.set("s", opts.InputEl.value.trim());
            queries.set("pp", opts.Settings.postsPerPage.toString());

            // Types
            const checkedTypes = [];
            for (let e = 0; e < opts.TypesEls.length; e++) {
                if (opts.TypesEls[e].checked) checkedTypes.push(opts.TypesEls[e].value);
            }
            if (checkedTypes.length > 0) {
                queries.set("t", checkedTypes.join(","));
            }
            else if (opts.Settings.postTypes.length > 0) {
                // If no checkboxes (single post type), use all post types from settings
                queries.set("t", opts.Settings.postTypes.join(","));
            }

            // Dates
            if ((opts.DateStartEl !== null) && (opts.DateStartEl.value !== "")) {
                queries.set("ds", (DateTime.fromFormat(opts.DateStartEl.value, "yyyy-MM-dd")).startOf("day").toISO());
            }
            if ((opts.DateEndEl !== null) && (opts.DateEndEl.value !== "")) {
                queries.set("de", (DateTime.fromFormat(opts.DateEndEl.value, "yyyy-MM-dd")).endOf("day").toISO());
            }

            // Pagination - note: page will only be set for pagination-initiated requests
            if (page === undefined) {
                EmptyElement(opts.PaginationButtonsEl);
            }
            else {
                queries.set("p", page.toString());
            }

            const url = opts.Settings.restBaseUrl
                + "/" + opts.Settings.restSearchPath
                + (queries.size < 1 ? "" : "?" + queries.toString());

            // JS-6: guard against out-of-order responses. Searches fire on checkbox/date change,
            // Go, Enter and pagination, so requests can overlap; a slow earlier response must not
            // clobber the results of a newer one. Bail if a newer request has since started.
            opts._reqSeq = (opts._reqSeq || 0) + 1;
            const mySeq = opts._reqSeq;

            // Run Ajax request
            Rest(REST_VERBS.Get, url, {}, function (successful, packet, response) {
                if (mySeq !== opts._reqSeq) return; // superseded by a newer search
                EmptyElement(opts.ResultsEl);
                if (!successful) {
                    // ESC-4: show the visitor a friendly message; keep the raw payload in the console.
                    if (typeof console !== "undefined") console.error("ADF Search: search request failed", packet);
                    opts.ResultsEl.appendChild(GetResultsMessageElement("Sorry, an error occurred when trying to run your search. Please try again.", ["text-danger"], false));
                }
                else {
                    // JS-5: a missing/garbage X-WP-Total header must not become NaN in the pagination math.
                    const rawTotal = "getResponseHeader" in response
                        ? response.getResponseHeader(HEADER_KEY.WpTotal)
                        : response.headers.get(HEADER_KEY.WpTotal);
                    const wpTotal = parseInt(rawTotal, 10) || 0;

                    DisplaySearchResults(page, searchText, packet, wpTotal, opts);
                }

                // Remove styles that kept results height
                opts.ResultsEl.style.height = "auto";
                opts.ResultsEl.style.overflow = "auto";

                if (Callback !== undefined) Callback(successful);
            });
        }
    }

    function DisplaySearchResults(page, searchText, response, wpTotal, opts) {
        if (!Array.isArray(response) || response.length < 1) {
            opts.ResultsEl.appendChild(GetResultsMessageElement("We didn't find any matching content, sorry!", [], false));
        }
        else {
            const ul = BuildElement("ul", "", ["list-group"]);
            for (let i = 0; i < response.length; i++) {
                ul.appendChild(GetSearchResultItemLi(response[i], searchText));
            }
            opts.ResultsEl.appendChild(ul);

            // Pagination
            if (page === undefined) {
                const paginationOpts = {
                    ItemsName: "result",
                    ButtonsContainerEl: opts.PaginationButtonsEl,
                    TotalItemCount: wpTotal,
                    CurrentItemCount: response.length,
                    MaxPageItemCount: opts.Settings.postsPerPage,
                    PageChangeCallback: function (pagOpts) {
                        RunAjaxSearch(opts, pagOpts.Page, function (successful) {
                            if (successful) Pagination.UpdateButtons(pagOpts);
                            else EmptyElement(pagOpts.ButtonsContainerEl);
                        });
                    }
                };

                Pagination.Initialize(paginationOpts);
                Pagination.UpdateButtons(paginationOpts);
            }
        }
    }

    function GetSearchResultItemLi(item, searchText) {
        const contentEls = [];

        const phrase = normalizeSearchText(searchText.trim());
        let terms = [];
        let markTerms = [];
        if (phrase !== "") {
            terms = phrase.replace(/\s+/g, " ").trim().toLowerCase().split(" ");
            markTerms = item.match === "exact" ? [phrase] : terms;
        }

        const matchEl = GetSearchMatchTypeElement(item);
        if (matchEl) contentEls.push(matchEl);

        const typeEl = GetSearchResultItemTypeElement(item);
        if (typeEl) contentEls.push(typeEl);

        const dateEl = GetSearchResultDateElement(item);
        if (dateEl) contentEls.push(dateEl);

        if ("title" in item) contentEls.push(BuildElement("h5", "", ["py-2", "mb-0"], {}, GetMarkedElements(item.title, markTerms, "", "", BuildElement)));

        const summaryEl = GetSearchResultSummaryElement(item, searchText, terms, markTerms);
        if (summaryEl) contentEls.push(summaryEl);

        return BuildElement("li", "", ["list-group-item", "p-0", "bg-white"], {}, [
            BuildElement("a", "", ["d-block", "p-3", "text-decoration-none", "adf-hover-bg-gray-100"], { href: item.url }, contentEls)
        ]);
    }

    function GetSearchMatchTypeElement(item) {
        let text = "";
        let bgClass = "";

        switch (item.match) {
            case "exact":
                text = "Match: Exact";
                bgClass = "success";
                break;

            case "all":
                text = "Match: All Terms";
                bgClass = "info";
                break;

            case "any":
                text = "Match: Any Terms";
                bgClass = "dark";
                break;
        }

        if (text === "") return false;
        return BuildElement("span", "", ["float-end", "badge", "rounded-pill", "text-bg-" + bgClass, "fw-normal"], {}, text);
    }

    function GetSearchResultItemTypeElement(item) {
        if ("type" in item) {
            return BuildElement("span", "", [SEARCH_CLASS.TypeBadge, "has-small-font-size", "p-2", "py-1", "me-2", "border", "border-1", "border-dark", "rounded", "bg-dark", "text-white", "text-uppercase", "fw-bold"], { style: "letter-spacing: 0.08em" }, item.type);
        }

        return false;
    }

    function GetSearchResultDateElement(item) {
        let text = "";

        if ("date" in item) {
            const date = DateTime.fromFormat(item.date, "yyyy-MM-dd hh:mm:ss");
            text = date.toFormat("MM/dd/yyyy");
        }

        if (text === "") return false;
        return BuildElement("span", "", ["adf-publish-date", "text-secondary"], { style: "font-size: 1.1rem" }, text);
    }

    function GetSearchResultSummaryElement(item, searchText, terms, markTerms) {
        if ("content" in item) {
            if (("match" in item) && (item.match === "exact")) terms = [searchText];

            return BuildElement("p", "", ["text-secondary", "mb-0"], { style: "font-size: 0.9rem" }, GetSearchSummaryElements(item.content, terms, markTerms, 200, BuildElement));
        }

        return false;
    }

    function GetResultsMessageElement(msg, classes, classesAreRelacements) {
        if (!classesAreRelacements) classes.push("p-2", "text-center");
        return BuildElement("p", "", classes, {}, msg);
    }

})(window);
